"use strict";

(() => {
  const MARKER = "CODEX_SHOP_ALL_CALLOUT_LENS_FAMILY_V2_20260630";
  if (window.__twoNuCalloutLensFamilyV2Ready) return;
  window.__twoNuCalloutLensFamilyV2Ready = true;

  const FAMILY_ORDER = ["tvo", "tvo_pro", "hex", "pre", "hex_pre"];
  const FEATURE_ORDER = ["uv400", "polarized", "hexachroma", "metonic", "flexfoam", "float_max", "prescription"];
  const HEAD_SHAPE_ORDER = ["standard_small", "standard", "standard_wide", "wide_fit"];
  const ACTIVITY_KEYS = ["running", "trail", "water", "cycling", "driving", "golf", "everyday", "alpine"];
  const PRESCRIPTION_FAMILIES = new Set(["pre", "hex_pre"]);
  const ACTIVITY_DEFAULT_FAMILY_ORDER = ["hex", "tvo_pro", "tvo", "pre"];
  const MULTI_ACTIVITY_DEFAULT_FAMILY_ORDER = ["hex", "tvo_pro", "tvo"];

  const escapeHtml = (value) => String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

  const parseJson = (node) => {
    try {
      return JSON.parse(node?.textContent || "{}");
    } catch (error) {
      console.error("Callout lens family v2 JSON parse failed", { error, marker: MARKER });
      return {};
    }
  };

  const isObject = (value) => value && typeof value === "object" && !Array.isArray(value);

  const familyHasSkus = (family) => Array.isArray(family?.skus) && family.skus.some(Boolean);

  const variantBelongsTo = (variant, familyKey) => Array.isArray(variant?.families) && variant.families.includes(familyKey);

  const visibleFamilies = (data) => FAMILY_ORDER.filter((key) => familyHasSkus(data?.families?.[key]));

  /* CODEX_CALLOUT_CHEAPEST_DEFAULT_20260723 */
  const cheapestAvailableVariant = (data) => {
    const families = new Set(visibleFamilies(data));
    return (data?.variants || []).reduce((cheapest, variant) => {
      const price = Number(variant?.price);
      const eligible = variant?.available === true
        && Number.isFinite(price)
        && Array.isArray(variant?.families)
        && variant.families.some((familyKey) => families.has(familyKey));
      if (!eligible) return cheapest;
      if (!cheapest) return variant;
      return price < Number(cheapest.price) ? variant : cheapest;
    }, null);
  };

  const firstFamily = (data, preferredVariant = null) => {
    const families = visibleFamilies(data);
    const variant = preferredVariant || cheapestAvailableVariant(data);
    const cheapestFamily = families.find((familyKey) => variantBelongsTo(variant, familyKey));
    return cheapestFamily || families[0] || "";
  };

  const activityMatchEnabledFor = (item) => {
    const root = item?.closest?.("[data-callout-lens-family-v2]");
    return root?.dataset.calloutActivityMatchFilterRequested === "true"
      && root?.dataset.calloutActivityMatchFilterActive === "true";
  };

  /* CODEX_V1513_ACTIVITY_SCORE_DEFAULT_SORT_20260807 */
  const activityScoreKeysFor = (item) => {
    const root = item?.closest?.("[data-callout-lens-family-v2]");
    return String(root?.dataset.calloutActivityScoreKeys || "")
      .split(",")
      .map((key) => key.trim())
      .filter((key) => ACTIVITY_KEYS.includes(key));
  };

  const activityScoreTotalFor = (variant, scoreKeys) => scoreKeys.reduce((total, key) => {
    const score = Number(variant?.activityScores?.[key]);
    return Number.isFinite(score) ? total + score : total;
  }, 0);

  const sortByActivityScore = (item, variants) => {
    const root = item?.closest?.("[data-callout-lens-family-v2]");
    if (root?.dataset.calloutActivityScoreSortFallback !== "true") return variants;
    const scoreKeys = activityScoreKeysFor(item);
    if (!scoreKeys.length) return variants;
    return variants
      .map((variant, stableOrder) => ({ variant, stableOrder }))
      .sort((left, right) => (
        activityScoreTotalFor(right.variant, scoreKeys)
        - activityScoreTotalFor(left.variant, scoreKeys)
        || left.stableOrder - right.stableOrder
      ))
      .map((entry) => entry.variant);
  };

  const multiActivityMatchEnabledFor = (item) => {
    const root = item?.closest?.("[data-callout-lens-family-v2]");
    return activityMatchEnabledFor(item)
      && root?.dataset.calloutActivityMatchMulti === "true";
  };

  const variantIsNonPrescription = (variant) => (
    Array.isArray(variant?.families)
    && !variant.families.some((familyKey) => PRESCRIPTION_FAMILIES.has(familyKey))
  );

  const multiActivityDefaultSelection = (item, data) => {
    if (!multiActivityMatchEnabledFor(item)) return null;

    const visible = new Set(visibleFamilies(data));
    const rankedAvailable = (data?.variants || []).filter((variant) => (
      variant?.available === true
      && variantIsNonPrescription(variant)
    ));

    for (const familyKey of MULTI_ACTIVITY_DEFAULT_FAMILY_ORDER) {
      if (!visible.has(familyKey)) continue;
      const variant = rankedAvailable.find((candidate) => variantBelongsTo(candidate, familyKey));
      if (variant) return { variant, familyKey };
    }

    return null;
  };

  const activityFamilyForVariant = (data, variant) => {
    const visible = new Set(visibleFamilies(data));
    return ACTIVITY_DEFAULT_FAMILY_ORDER.find((familyKey) => (
      visible.has(familyKey)
      && variantBelongsTo(variant, familyKey)
    )) || "";
  };

  const activityDefaultSelection = (item, data) => {
    if (!activityMatchEnabledFor(item)) return null;
    if (multiActivityMatchEnabledFor(item)) {
      return multiActivityDefaultSelection(item, data);
    }

    const available = sortByActivityScore(item, (data?.variants || []).filter((variant) => (
      variant?.available === true
      && activityFamilyForVariant(data, variant)
    )));
    const preferredId = String(
      item.dataset.calloutV2ActivityPreferredVariantId
      || data?.activityMatchPreferredVariantId
      || ""
    );
    const preferred = preferredId
      ? available.find((variant) => String(variant.id) === preferredId)
      : null;
    if (preferred) {
      return {
        variant: preferred,
        familyKey: activityFamilyForVariant(data, preferred),
      };
    }

    for (const familyKey of ["hex", "tvo_pro"]) {
      const variant = available.find((candidate) => variantBelongsTo(candidate, familyKey));
      if (variant) return { variant, familyKey };
    }

    const fallback = available.reduce((cheapest, variant) => {
      const price = Number(variant?.price);
      if (!Number.isFinite(price)) return cheapest;
      if (!cheapest) return variant;
      return price < Number(cheapest.price) ? variant : cheapest;
    }, null);
    return fallback
      ? { variant: fallback, familyKey: activityFamilyForVariant(data, fallback) }
      : null;
  };

  const defaultSelectionFor = (item, data) => {
    if (activityMatchEnabledFor(item)) {
      return activityDefaultSelection(item, data) || {
        variant: null,
        familyKey: "",
      };
    }
    const variant = cheapestAvailableVariant(data);
    return {
      variant,
      familyKey: firstFamily(data, variant),
    };
  };

  const sharedI18nFor = (item) => {
    const root = item.closest("[data-callout-lens-family-v2]");
    if (!root) return {};
    if (!isObject(root.__calloutLensFamilyV2I18n)) {
      root.__calloutLensFamilyV2I18n = parseJson(
        root.querySelector("[data-callout-lens-family-v2-i18n]")
      );
    }
    return root.__calloutLensFamilyV2I18n;
  };

  const cardData = (item) => {
    if (!item.__calloutLensFamilyV2Data) {
      const data = parseJson(item.querySelector("[data-callout-lens-family-v2-data]"));
      if (!isObject(data.i18n)) data.i18n = sharedI18nFor(item);
      item.__calloutLensFamilyV2Data = data;
    }
    return item.__calloutLensFamilyV2Data;
  };

  const cardElement = (item) => item.querySelector(".card-js");
  const swatchesElement = (card) => card?.querySelector?.(".swatches");
  const activeSwatch = (card) => card?.querySelector?.(".swatch-wrapper.active:not([hidden])");
  const buyButton = (item) => item.querySelector(".buy-button");
  const buyButtonText = (item) => item.querySelector(".buy-button-text");
  const aiTryOnButton = (item) => item.querySelector("[data-ai-tryon-trigger]");

  const normalizeIds = (value) => String(value || "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);

  const variantsById = (data) => {
    if (!data.__variantsById) {
      data.__variantsById = new Map((data.variants || []).map((variant) => [String(variant.id), variant]));
    }
    return data.__variantsById;
  };

  const activeVariant = (item) => {
    const data = cardData(item);
    const card = cardElement(item);
    const alpine = card?._x_dataStack?.[0];
    const id = String(alpine?.id || activeSwatch(card)?.dataset.ids?.split(",")?.[0] || "");
    return variantsById(data).get(id) || null;
  };

  const familyVariantForSwatch = (swatch, data, familyKey) => {
    const map = variantsById(data);
    const originalIds = normalizeIds(swatch.dataset.calloutV2OriginalIds || swatch.dataset.ids);
    return originalIds
      .map((id) => map.get(String(id)))
      .find((variant) => variantBelongsTo(variant, familyKey)) || null;
  };

  const variantRankById = (data) => new Map(
    (data?.variants || []).map((variant, index) => [String(variant.id), index])
  );

  const rankedFamilyVariantForSwatch = (swatch, data, familyKey, rankById) => {
    const map = variantsById(data);
    return normalizeIds(swatch.dataset.calloutV2OriginalIds || swatch.dataset.ids)
      .map((id) => map.get(String(id)))
      .filter((variant) => (
        variant?.available === true
        && variantBelongsTo(variant, familyKey)
      ))
      .sort((left, right) => (
        (rankById.get(String(left.id)) ?? Number.MAX_SAFE_INTEGER)
        - (rankById.get(String(right.id)) ?? Number.MAX_SAFE_INTEGER)
      ))[0] || null;
  };

  const originalSwatchOrderFor = (card, swatches) => {
    if (!(card.__calloutV2OriginalSwatchOrder instanceof Map)) {
      card.__calloutV2OriginalSwatchOrder = new Map();
    }
    const order = card.__calloutV2OriginalSwatchOrder;
    swatches.forEach((swatch) => {
      if (!order.has(swatch)) order.set(swatch, order.size);
    });
    return order;
  };

  const pinMultiActivityInitialSwatch = (card, target) => {
    if (!card || !target) return;
    card.querySelectorAll(".swatch-wrapper[first-button]").forEach((swatch) => {
      swatch.removeAttribute("first-button");
    });
    target.setAttribute("first-button", "");
  };

  const userChoiceFor = (item, data) => {
    if (!multiActivityMatchEnabledFor(item)) return null;
    const choice = item.__calloutV2TrustedUserChoice;
    if (!choice?.familyKey || !choice?.variantId) return null;
    if (!visibleFamilies(data).includes(choice.familyKey)) return null;

    const variant = variantsById(data).get(String(choice.variantId));
    if (
      variant?.available !== true
      || !variantBelongsTo(variant, choice.familyKey)
    ) return null;

    const card = cardElement(item);
    const mappedSwatch = Array.from(card?.querySelectorAll?.(".swatch-wrapper") || [])
      .find((swatch) => normalizeIds(
        swatch.dataset.calloutV2OriginalIds || swatch.dataset.ids
      ).includes(String(choice.variantId)));
    if (!mappedSwatch) return null;

    return {
      familyKey: choice.familyKey,
      variant,
    };
  };

  const rememberTrustedUserChoice = (item, familyKey, variant) => {
    if (
      !multiActivityMatchEnabledFor(item)
      || !familyKey
      || !variant?.id
      || !variantBelongsTo(variant, familyKey)
    ) return;
    item.__calloutV2TrustedUserChoice = {
      familyKey,
      variantId: String(variant.id),
    };
  };

  const clearInvalidTrustedUserChoice = (item, data) => {
    if (!item.__calloutV2TrustedUserChoice) return;
    if (!userChoiceFor(item, data)) delete item.__calloutV2TrustedUserChoice;
  };

  const swatchColorKey = (swatch) => [
    String(swatch?.dataset.frameColor || ""),
    String(swatch?.dataset.lensesColor || ""),
  ].join("|");

  /* CODEX_V1423_CALLOUT_ACTIONS_BEFORE_HEADFIT_20260713 */
  const ensureControlsPlacement = (item) => {
    const controls = item.querySelector("[data-callout-lens-family-v2-controls]");
    const card = cardElement(item);
    if (!controls || !card) return;

    if (controls.parentElement !== card) card.appendChild(controls);

    const actions = card.querySelector(":scope > .card__callout-actions") || controls.querySelector(":scope > .card__callout-actions");
    const fit = controls.querySelector(":scope > .callout-lens-family-v2__fit");
    if (actions && actions.parentElement !== controls) controls.insertBefore(actions, fit || controls.children[1] || null);

    controls.dataset.calloutV2Moved = "true";
    controls.dataset.calloutV2Placement = "lens-actions-fit";
  };

  const ensureOriginalSwatchIds = (card) => {
    card?.querySelectorAll?.(".swatch-wrapper").forEach((swatch) => {
      if (!swatch.dataset.calloutV2OriginalIds) {
        swatch.dataset.calloutV2OriginalIds = swatch.dataset.ids || "";
      }
    });
  };

  const actionForFamily = (data, familyKey) => data?.families?.[familyKey]?.action || (PRESCRIPTION_FAMILIES.has(familyKey) ? "prescription" : "cart");

  const featureState = (data, familyKey) => {
    const productFeatures = data.productFeatures || {};
    const family = data.families?.[familyKey] || {};
    return {
      uv400: true,
      polarized: familyKey !== "tvo",
      hexachroma: familyKey === "hex" || familyKey === "hex_pre",
      metonic: productFeatures.metonic !== false,
      flexfoam: productFeatures.flexfoam !== false,
      float_max: family.floatMax === true,
      prescription: PRESCRIPTION_FAMILIES.has(familyKey),
    };
  };

  const keepUnavailableFeatureVisible = (item, data, key) => {
    const section = item?.closest?.("[data-callout-lens-family-v2]");
    return section?.id === "hexachroma-products"
      && key === "float_max"
      && familyHasSkus(data?.families?.hex_pre);
  };

  const featureButtonLabel = (data, key, available) => {
    const labels = data.i18n?.features || {};
    const status = available ? (data.i18n?.included || "Included") : (data.i18n?.notIncluded || "Not included");
    return `${labels[key] || key}: ${status}`;
  };

  let featureMeasureCanvas = null;

  const featureMeasureContext = () => {
    if (!featureMeasureCanvas) featureMeasureCanvas = document.createElement("canvas");
    return featureMeasureCanvas.getContext("2d");
  };

  const fitFeatureLabelFont = (item) => {
    const rows = Array.from(item?.querySelectorAll?.("[data-callout-v2-feature-row]") || [])
      .filter((row) => !row.hidden);
    if (!rows.length) return;
    const labelNodes = rows
      .map((row) => row.querySelector(".tag__text"))
      .filter((node) => node && node.textContent.trim());
    const labelCells = rows
      .map((row) => row.querySelector(".callout-lens-family-v2__feature-label"))
      .filter(Boolean);
    if (!labelNodes.length || !labelCells.length) return;

    const availableWidths = labelCells
      .map((cell) => cell.getBoundingClientRect().width)
      .filter((width) => Number.isFinite(width) && width > 24);
    if (!availableWidths.length) return;

    const context = featureMeasureContext();
    if (!context) return;
    const sampleStyle = window.getComputedStyle(rows[0]);
    const fontFamily = sampleStyle.fontFamily || "Inter, Arial, sans-serif";
    const fontWeight = sampleStyle.fontWeight || "800";
    const available = Math.min(...availableWidths) - 1;
    const maxSize = 14;
    const minSize = 10.5;
    let chosen = minSize;

    for (let size = maxSize; size >= minSize; size -= 0.2) {
      context.font = `${fontWeight} ${size.toFixed(1)}px ${fontFamily}`;
      const fits = labelNodes.every((node) => context.measureText(node.textContent.trim()).width <= available);
      if (fits) {
        chosen = size;
        break;
      }
    }

    item.style.setProperty("--callout-v2-feature-font-size", `${Math.round(chosen * 10) / 10}px`);
  };

  const scheduleFeatureLabelFontFit = (item) => {
    if (!item) return;
    if (item.__calloutV2FeatureFontFrame) window.cancelAnimationFrame(item.__calloutV2FeatureFontFrame);
    item.__calloutV2FeatureFontFrame = window.requestAnimationFrame(() => {
      item.__calloutV2FeatureFontFrame = null;
      fitFeatureLabelFont(item);
    });
  };

  const forceFeatureLabelFontFit = (item) => {
    syncFeaturePlainLabels(item);
    scheduleFeatureLabelFontFit(item);
    window.setTimeout(() => scheduleFeatureLabelFontFit(item), 250);
    window.setTimeout(() => scheduleFeatureLabelFontFit(item), 1200);
  };

  const observeFeatureLabelFit = (item) => {
    if (!item || item.__calloutV2FeatureResizeObserver || !window.ResizeObserver) return;
    const observer = new ResizeObserver(() => scheduleFeatureLabelFontFit(item));
    observer.observe(item);
    const holder = item.querySelector("[data-callout-v2-features]");
    if (holder) observer.observe(holder);
    item.__calloutV2FeatureResizeObserver = observer;
  };

  const syncFeaturePlainLabels = (item) => {
    item?.querySelectorAll?.("[data-callout-v2-feature-row]").forEach((row) => {
      const holder = row.querySelector(".callout-lens-family-v2__feature-label");
      const textNode = row.querySelector(".tag__text");
      if (!holder || !textNode) return;
      const text = Array.from(textNode.childNodes)
        .filter((node) => node.nodeType === Node.TEXT_NODE)
        .map((node) => node.textContent)
        .join(" ")
        .replace(/\s+/g, " ")
        .trim() || textNode.textContent.replace(/\s+/g, " ").trim();
      holder.dataset.calloutV2PlainLabel = text;
    });
  };

  const renderFeatureMatrix = (item, familyKey) => {
    const data = cardData(item);
    const holder = item.querySelector("[data-callout-v2-features]");
    if (!holder) return;
    const states = featureState(data, familyKey);
    const hideUnavailable = visibleFamilies(data).length <= 1;
    FEATURE_ORDER.forEach((key) => {
      const row = holder.querySelector(`[data-callout-v2-feature-row="${key}"]`);
      if (!row) return;
      const available = states[key] === true;
      const title = available ? (data.i18n?.included || "Included") : (data.i18n?.notIncluded || "Not included");
      const icon = row.querySelector("[data-callout-v2-feature-icon]");
      row.hidden = hideUnavailable && !available && !keepUnavailableFeatureVisible(item, data, key);
      row.dataset.calloutV2Available = available ? "true" : "false";
      row.setAttribute("aria-label", featureButtonLabel(data, key, available));
      row.classList.toggle("callout-lens-family-v2__feature--yes", available);
      row.classList.toggle("callout-lens-family-v2__feature--no", !available);
      if (icon) {
        icon.classList.toggle("callout-lens-family-v2__feature-icon--yes", available);
        icon.classList.toggle("callout-lens-family-v2__feature-icon--no", !available);
        icon.removeAttribute("aria-label");
        icon.textContent = available ? "✓" : "✕";
      }
    });
    scheduleFeatureLabelFontFit(item);
  };

  const activeHeadShapes = (fitLabel) => {
    const text = String(fitLabel || "").toLowerCase();
    if (!text) return [];
    if (text.includes("flexible")) return [...HEAD_SHAPE_ORDER];
    if (text.includes("smaller") || text.includes("small")) return ["standard_small", "standard"];
    if (text.includes("normal") && text.includes("larger")) return ["standard", "standard_wide", "wide_fit"];
    if (text.includes("larger") || text.includes("large")) return ["standard_wide", "wide_fit"];
    if (text.includes("normal")) return ["standard_small", "standard", "standard_wide"];
    return ["standard_small", "standard", "standard_wide"];
  };

  const headOptionMarkup = (key, labels, activeShapes) => `
    <span class="callout-lens-family-v2__head-option callout-lens-family-v2__head-option--${key.replace(/_/g, "-")}${activeShapes.has(key) ? " callout-lens-family-v2__head-option--active" : ""}" aria-label="${escapeHtml(labels[key] || key)}">
      <span class="callout-lens-family-v2__head-icon" aria-hidden="true"></span>
      <span class="callout-lens-family-v2__head-label">${escapeHtml(labels[key] || key)}</span>
    </span>
  `;

  const activeHeadLabels = (data) => {
    const labels = data.i18n?.fitOptions || {};
    const activeShapes = new Set(activeHeadShapes(data.fitLabel));
    return HEAD_SHAPE_ORDER
      .filter((key) => activeShapes.has(key))
      .map((key) => labels[key] || key);
  };

  const renderFitLabel = (item) => {
    const data = cardData(item);
    const holder = item.querySelector("[data-callout-v2-fit]");
    if (!holder) return;
    const activeShapes = new Set(activeHeadShapes(data.fitLabel));
    const titleNode = holder.querySelector("[data-callout-v2-fit-title]");
    const button = holder.querySelector("[data-callout-v2-fit-options]");
    if (activeShapes.size === 0) {
      holder.hidden = true;
      if (titleNode) titleNode.textContent = "";
      if (button) button.innerHTML = "";
      return;
    }
    const labels = data.i18n?.fitOptions || {};
    const title = data.i18n?.fitSystemLabel || data.i18n?.fitLabel || "Fit";
    const fitSummary = activeHeadLabels(data).join(", ");
    if (titleNode) titleNode.textContent = title;
    if (button) {
      button.innerHTML = HEAD_SHAPE_ORDER.map((key) => headOptionMarkup(key, labels, activeShapes)).join("");
      button.setAttribute("aria-label", `${title}: ${fitSummary}`);
    }
    holder.hidden = false;
  };

  const renderLimitLabel = (item, familyKey) => {
    const data = cardData(item);
    const holder = item.querySelector("[data-callout-v2-limit]");
    if (!holder) return;
    const limit = String(data.families?.[familyKey]?.limitLabel || "").trim();
    if (!limit) {
      holder.hidden = true;
      holder.textContent = "";
      return;
    }
    const prefix = data.i18n?.prescriptionLimitLabel || "Prescription limit";
    holder.textContent = `${prefix}: ${limit}`;
    holder.hidden = false;
  };

  const activityAxes = (data) => {
    const labels = data.i18n?.activityRadar?.axes || {};
    return ACTIVITY_KEYS.map((key) => ({
      key,
      label: labels[key] || ({
        running: "Run",
        trail: "Trail",
        water: "Water",
        cycling: "Cycle",
        driving: "Drive",
        golf: "Golf",
        everyday: "Daily",
        alpine: "ALPINE",
      })[key] || key,
    }));
  };

  const normalizeActivityScores = (variant, axes) => {
    const raw = variant?.activityScores;
    if (!isObject(raw)) return null;
    const scores = {};
    let hasScore = false;
    axes.forEach((axis) => {
      const value = Number(raw[axis.key]);
      scores[axis.key] = Number.isFinite(value) ? Math.max(0, Math.min(5, value)) : 0;
      if (scores[axis.key] > 0) hasScore = true;
    });
    return hasScore ? scores : null;
  };

  const zeroActivityScores = (axes) => Object.fromEntries(axes.map((axis) => [axis.key, 0]));

  const cloneScores = (scores, axes) => Object.fromEntries(axes.map((axis) => [axis.key, Number(scores?.[axis.key]) || 0]));

  const scoresEqual = (a, b, axes) => axes.every((axis) => Math.abs((Number(a?.[axis.key]) || 0) - (Number(b?.[axis.key]) || 0)) < 0.015);

  const pendingRadarVariant = (item, data) => {
    const pendingId = item.__calloutV2PendingVariantId;
    if (!pendingId) return null;
    return variantsById(data).get(String(pendingId)) || null;
  };

  const clearPendingRadarVariant = (item) => {
    const pendingId = item.__calloutV2PendingVariantId;
    if (!pendingId) return;
    const active = activeVariant(item);
    if (!active || String(active.id) === String(pendingId)) {
      item.__calloutV2PendingVariantId = "";
    }
  };

  const nextInteractionToken = (item) => {
    item.__calloutV2InteractionToken = (Number(item.__calloutV2InteractionToken) || 0) + 1;
    return item.__calloutV2InteractionToken;
  };

  const isCurrentInteraction = (item, token) => item.__calloutV2InteractionToken === token;

  const radarPointAt = (center, radius, axes, index, score = 5) => {
    const angle = (-Math.PI / 2) + ((Math.PI * 2 * index) / axes.length);
    const scaledRadius = radius * (score / 5);
    return {
      x: center + Math.cos(angle) * scaledRadius,
      y: center + Math.sin(angle) * scaledRadius,
    };
  };

  const drawRadarPolygon = (context, points) => {
    context.beginPath();
    points.forEach((point, index) => {
      if (index === 0) context.moveTo(point.x, point.y);
      else context.lineTo(point.x, point.y);
    });
    context.closePath();
  };

  const activityRadarPalette = (canvas) => {
    const root = canvas.closest("[data-callout-lens-family-v2]") || canvas.closest("[data-callout-lens-family-v2-item]") || canvas;
    const styles = window.getComputedStyle(root);
    const value = (name, fallback) => styles.getPropertyValue(name).trim() || fallback;
    return {
      accent: value("--callout-v2-radar-accent", "#0BF477"),
      accentRgb: value("--callout-v2-radar-accent-rgb", "11, 244, 119"),
      brand: value("--callout-v2-radar-brand", "#004449"),
      brandRgb: value("--callout-v2-radar-brand-rgb", "0, 68, 73"),
      midRgb: value("--callout-v2-radar-mid-rgb", "0, 102, 91"),
      dot: value("--callout-v2-radar-dot", "rgba(239, 255, 246, .92)"),
    };
  };

  const radarRgba = (triplet, alpha) => `rgba(${triplet}, ${alpha})`;

  const drawActivityRadar = (canvas, data, scores) => {
    const palette = activityRadarPalette(canvas);
    const axes = activityAxes(data);
    const rect = canvas.getBoundingClientRect();
    const cssSize = Math.max(124, Math.round(Math.min(rect.width || 160, 196)));
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    canvas.width = Math.round(cssSize * dpr);
    canvas.height = Math.round(cssSize * dpr);
    canvas.style.height = cssSize + "px";

    const context = canvas.getContext("2d");
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    context.clearRect(0, 0, cssSize, cssSize);

    const center = cssSize / 2;
    const radius = cssSize * 0.292;
    const outerPlateRadius = cssSize * 0.43;
    const labelRadius = cssSize * 0.392;
    const basePoints = axes.map((axis, index) => radarPointAt(center, radius, axes, index, 5));
    const outerPlatePoints = axes.map((axis, index) => radarPointAt(center, outerPlateRadius, axes, index, 5));

    context.save();
    drawRadarPolygon(context, outerPlatePoints);
    context.shadowColor = radarRgba(palette.brandRgb, .14);
    context.shadowBlur = Math.max(3, cssSize * .032);
    context.shadowOffsetY = Math.max(1, cssSize * .012);
    const plateGradient = context.createRadialGradient(center, center, cssSize * .08, center, center, outerPlateRadius);
    plateGradient.addColorStop(0, "rgba(247, 249, 248, .34)");
    plateGradient.addColorStop(0.58, "rgba(226, 233, 230, .58)");
    plateGradient.addColorStop(1, "rgba(202, 213, 209, .74)");
    context.fillStyle = plateGradient;
    context.fill();
    context.restore();

    context.save();
    context.lineWidth = Math.max(1, cssSize * .006);
    outerPlatePoints.forEach((point, index) => {
      const next = outerPlatePoints[(index + 1) % outerPlatePoints.length];
      context.beginPath();
      context.moveTo(point.x, point.y);
      context.lineTo((point.x * .18) + (next.x * .82), (point.y * .18) + (next.y * .82));
      context.strokeStyle = "rgba(255, 255, 255, .18)";
      context.stroke();
    });
    context.restore();

    context.save();
    drawRadarPolygon(context, basePoints);
    const coreGradient = context.createRadialGradient(center, center, radius * 0.04, center, center, radius);
    coreGradient.addColorStop(0, radarRgba(palette.brandRgb, .34));
    coreGradient.addColorStop(1, radarRgba(palette.brandRgb, .06));
    context.fillStyle = coreGradient;
    context.fill();

    [1, 2, 3, 4, 5].forEach((level) => {
      const ringPoints = axes.map((axis, index) => radarPointAt(center, radius, axes, index, level));
      drawRadarPolygon(context, ringPoints);
      context.lineWidth = level === 5 ? Math.max(1.2, cssSize * .011) : Math.max(.7, cssSize * .0055);
      context.strokeStyle = level === 5 ? radarRgba(palette.brandRgb, .30) : radarRgba(palette.brandRgb, .13);
      context.stroke();
    });

    basePoints.forEach((point) => {
      context.beginPath();
      context.moveTo(center, center);
      context.lineTo(point.x, point.y);
      context.lineWidth = Math.max(.7, cssSize * .0055);
      context.strokeStyle = radarRgba(palette.brandRgb, .14);
      context.stroke();
    });
    context.restore();

    const scorePoints = axes.map((axis, index) => radarPointAt(center, radius, axes, index, scores[axis.key]));
    drawRadarPolygon(context, scorePoints);
    const scoreGradient = context.createRadialGradient(center, center, 0, center, center, radius);
    scoreGradient.addColorStop(0, radarRgba(palette.brandRgb, .86));
    scoreGradient.addColorStop(0.52, radarRgba(palette.midRgb, .78));
    scoreGradient.addColorStop(1, radarRgba(palette.accentRgb, .66));
    context.fillStyle = scoreGradient;
    context.fill();
    context.lineJoin = "round";
    context.lineWidth = Math.max(3, cssSize * .038);
    context.strokeStyle = radarRgba(palette.accentRgb, .15);
    context.stroke();
    context.lineWidth = Math.max(2, cssSize * .017);
    context.strokeStyle = palette.accent;
    context.shadowColor = radarRgba(palette.accentRgb, .70);
    context.shadowBlur = Math.max(4, cssSize * .032);
    context.stroke();
    context.shadowBlur = 0;

    context.save();
    scorePoints.forEach((point, index) => {
      const isMax = Number(scores[axes[index].key]) >= 4.95;
      context.beginPath();
      context.arc(point.x, point.y, Math.max(isMax ? 2.6 : 2, cssSize * (isMax ? .023 : .017)), 0, Math.PI * 2);
      context.fillStyle = palette.dot;
      context.fill();
      context.lineWidth = Math.max(1, cssSize * .01);
      context.strokeStyle = palette.accent;
      context.stroke();
    });
    context.restore();

    context.beginPath();
    context.arc(center, center, Math.max(2, cssSize * .018), 0, Math.PI * 2);
    context.fillStyle = palette.accent;
    context.fill();
    context.beginPath();
    context.arc(center, center, Math.max(6, cssSize * .05), 0, Math.PI * 2);
    context.strokeStyle = radarRgba(palette.accentRgb, .24);
    context.lineWidth = Math.max(1, cssSize * .008);
    context.stroke();

    const labelFontSize = Math.max(7, Math.min(10.2, cssSize * 0.058));
    const maxLabelWidth = cssSize * 0.34;
    context.textBaseline = "middle";
    axes.forEach((axis, index) => {
      const angle = (-Math.PI / 2) + ((Math.PI * 2 * index) / axes.length);
      const labelPoint = radarPointAt(center, labelRadius, axes, index);
      const score = Number(scores[axis.key]) || 0;
      const isMax = score >= 4.95;
      const text = String(axis.label || "").toLocaleUpperCase();
      context.font = (isMax ? "950 " : "850 ") + labelFontSize + "px Inter, Arial Black, Arial, sans-serif";
      const measuredWidth = Math.min(maxLabelWidth, context.measureText(text).width || maxLabelWidth);
      let rotation = angle + (Math.PI / 2);
      rotation = Math.atan2(Math.sin(rotation), Math.cos(rotation));
      if (rotation > Math.PI / 2) rotation -= Math.PI;
      if (rotation < -Math.PI / 2) rotation += Math.PI;
      const x = labelPoint.x;
      const y = labelPoint.y;
      context.save();
      context.translate(x, y);
      context.rotate(rotation);
      context.textAlign = "center";
      if (isMax) {
        const highlightWidth = Math.min(maxLabelWidth + (cssSize * 0.025), measuredWidth + (cssSize * 0.09));
        const highlightHeight = labelFontSize * 1.42;
        const highlightRadius = highlightHeight / 2;
        context.save();
        context.shadowColor = radarRgba(palette.accentRgb, .34);
        context.shadowBlur = Math.max(2, cssSize * .018);
        context.fillStyle = radarRgba(palette.accentRgb, .34);
        context.beginPath();
        if (typeof context.roundRect === "function") {
          context.roundRect(-highlightWidth / 2, -highlightHeight / 2, highlightWidth, highlightHeight, highlightRadius);
        } else {
          context.rect(-highlightWidth / 2, -highlightHeight / 2, highlightWidth, highlightHeight);
        }
        context.fill();
        context.restore();
      }
      context.fillStyle = palette.brand;
      const guideLength = Math.min(maxLabelWidth, measuredWidth + (cssSize * 0.045));
      context.beginPath();
      context.moveTo(-guideLength / 2, labelFontSize * 0.72);
      context.lineTo(guideLength / 2, labelFontSize * 0.72);
      context.lineWidth = Math.max(.7, cssSize * .0045);
      context.strokeStyle = isMax ? radarRgba(palette.brandRgb, .20) : radarRgba(palette.brandRgb, .22);
      context.stroke();
      if (isMax) {
        context.shadowColor = radarRgba(palette.accentRgb, .30);
        context.shadowBlur = Math.max(2, cssSize * .02);
      }
      context.fillText(text, 0, 0, maxLabelWidth);
      context.shadowBlur = 0;
      context.restore();
    });

    const i18n = data.i18n?.activityRadar || {};
    const outOfFive = i18n.outOfFive || "out of 5";
    const chartLabel = i18n.chartLabel || "Activity suitability radar chart";
    const scoresLabel = i18n.scoresLabel || "Activity suitability scores";
    const text = axes.map((axis) => axis.label + " " + Math.round(scores[axis.key] * 10) / 10 + " " + outOfFive).join(", ");
    canvas.setAttribute("aria-label", chartLabel + ": " + text);
    const summary = canvas.parentElement?.querySelector?.("[data-callout-v2-activity-radar-summary]");
    if (summary) summary.textContent = scoresLabel + ": " + text + ".";
  };

  const renderActivityRadar = (item, variantOverride = null, { immediate = false } = {}) => {
    const data = cardData(item);
    const radar = item.querySelector("[data-callout-v2-activity-radar]");
    const canvas = item.querySelector("[data-callout-v2-activity-radar-canvas]");
    if (!radar || !canvas) return;

    const axes = activityAxes(data);
    const variant = variantOverride || pendingRadarVariant(item, data) || activeVariant(item);
    const nextScores = normalizeActivityScores(variant, axes);
    if (!nextScores) {
      if (item.__calloutV2RadarFrame) window.cancelAnimationFrame(item.__calloutV2RadarFrame);
      radar.hidden = true;
      item.classList.remove("callout-lens-family-v2__item--has-radar");
      item.__calloutV2RadarScores = null;
      item.__calloutV2RadarRenderedScores = null;
      item.__calloutV2RadarTargetScores = null;
      item.__calloutV2RadarFrame = null;
      return;
    }

    radar.hidden = false;
    item.classList.add("callout-lens-family-v2__item--has-radar");
    if (
      !immediate
      && item.__calloutV2RadarFrame
      && item.__calloutV2RadarTargetScores
      && scoresEqual(item.__calloutV2RadarTargetScores, nextScores, axes)
    ) {
      return;
    }

    const previousScores = item.__calloutV2RadarRenderedScores || item.__calloutV2RadarScores || zeroActivityScores(axes);
    if (item.__calloutV2RadarFrame) window.cancelAnimationFrame(item.__calloutV2RadarFrame);

    if (immediate || window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) {
      drawActivityRadar(canvas, data, nextScores);
      item.__calloutV2RadarScores = cloneScores(nextScores, axes);
      item.__calloutV2RadarRenderedScores = cloneScores(nextScores, axes);
      item.__calloutV2RadarTargetScores = null;
      clearPendingRadarVariant(item);
      return;
    }

    if (scoresEqual(previousScores, nextScores, axes)) {
      drawActivityRadar(canvas, data, nextScores);
      item.__calloutV2RadarScores = cloneScores(nextScores, axes);
      item.__calloutV2RadarRenderedScores = cloneScores(nextScores, axes);
      item.__calloutV2RadarTargetScores = null;
      clearPendingRadarVariant(item);
      return;
    }

    const startedAt = performance.now();
    const duration = 420;
    item.__calloutV2RadarTargetScores = cloneScores(nextScores, axes);
    const animate = (now) => {
      const progress = Math.min(1, Math.max(0, (now - startedAt) / duration));
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = {};
      axes.forEach((axis) => {
        const from = Number(previousScores[axis.key]) || 0;
        const to = Number(nextScores[axis.key]) || 0;
        current[axis.key] = from + ((to - from) * eased);
      });
      drawActivityRadar(canvas, data, current);
      item.__calloutV2RadarRenderedScores = cloneScores(current, axes);
      if (progress < 1) {
        item.__calloutV2RadarFrame = window.requestAnimationFrame(animate);
      } else {
        item.__calloutV2RadarScores = cloneScores(nextScores, axes);
        item.__calloutV2RadarRenderedScores = cloneScores(nextScores, axes);
        item.__calloutV2RadarTargetScores = null;
        item.__calloutV2RadarFrame = null;
        clearPendingRadarVariant(item);
      }
    };
    item.__calloutV2RadarFrame = window.requestAnimationFrame(animate);
  };

  const setFamilyButtons = (item, familyKey) => {
    item.querySelectorAll("[data-callout-v2-family]").forEach((button) => {
      const active = button.dataset.calloutV2Family === familyKey;
      button.setAttribute("aria-pressed", active ? "true" : "false");
    });
  };

  const setButtonLabel = (item, familyKey) => {
    const data = cardData(item);
    const action = actionForFamily(data, familyKey);
    const label = action === "prescription"
      ? (data.i18n?.buttons?.orderNow || "Order Now")
      : (data.i18n?.buttons?.addToCart || "Add to cart");
    const button = buyButton(item);
    const text = buyButtonText(item);
    if (button) {
      button.dataset.label = label;
      button.dataset.calloutV2Action = action;
    }
    if (text) text.textContent = label;
  };

  const updateAiTryOn = (item) => {
    const variant = activeVariant(item);
    const button = aiTryOnButton(item);
    const data = cardData(item);
    if (!button || !variant) return;
    button.dataset.variantId = String(variant.id);
    if (variant.image) button.dataset.variantImage = variant.image;
    button.dataset.variantTitle = `${button.dataset.productTitle || ""} (${variant.title || variant.sku || ""})`.trim();
    button.dataset.variantLabel = variant.title || variant.sku || "";
    const card = cardElement(item);
    const alpine = card?._x_dataStack?.[0];
    if (alpine && variant.url) alpine.url = variant.url;
    if (data.productUrl && button.dataset.productTitle) {
      button.dataset.productHandle = data.productHandle || "";
    }
  };

  const applyFamilyToSwatches = (item, familyKey, preferredColorKey = "", preferredVariantId = "") => {
    const data = cardData(item);
    const card = cardElement(item);
    ensureOriginalSwatchIds(card);
    const swatches = Array.from(card?.querySelectorAll?.(".swatch-wrapper") || []);

    if (!multiActivityMatchEnabledFor(item)) {
      let preferredVariant = null;
      let preferred = null;
      let first = null;

      swatches.forEach((swatch) => {
        const variant = familyVariantForSwatch(swatch, data, familyKey);
        const visible = Boolean(variant);
        swatch.hidden = !visible;
        swatch.classList.toggle("callout-lens-family-v2__swatch-hidden", !visible);
        if (!visible) return;

        swatch.dataset.ids = String(variant.id);
        swatch.dataset.calloutV2Sku = variant.sku || "";
        if (!first) first = swatch;
        if (preferredVariantId && String(variant.id) === String(preferredVariantId)) preferredVariant = swatch;
        if (preferredColorKey && swatchColorKey(swatch) === preferredColorKey) preferred = swatch;
      });

      return preferredVariant || preferred || first;
    }

    const rankById = variantRankById(data);
    const originalOrder = originalSwatchOrderFor(card, swatches);
    const rows = swatches.map((swatch) => {
      const variant = rankedFamilyVariantForSwatch(swatch, data, familyKey, rankById);
      const visible = Boolean(variant);
      swatch.hidden = !visible;
      swatch.classList.toggle("callout-lens-family-v2__swatch-hidden", !visible);
      if (variant) {
        swatch.dataset.ids = String(variant.id);
        swatch.dataset.calloutV2Sku = variant.sku || "";
      }
      return {
        swatch,
        variant,
        visible,
        rank: variant
          ? (rankById.get(String(variant.id)) ?? Number.MAX_SAFE_INTEGER)
          : Number.MAX_SAFE_INTEGER,
        originalOrder: originalOrder.get(swatch) ?? Number.MAX_SAFE_INTEGER,
      };
    });

    const visibleRows = rows
      .filter((row) => row.visible)
      .sort((left, right) => (
        left.rank - right.rank
        || left.originalOrder - right.originalOrder
      ));
    const hiddenRows = rows
      .filter((row) => !row.visible)
      .sort((left, right) => left.originalOrder - right.originalOrder);
    const container = swatchesElement(card);
    if (container) {
      [...visibleRows, ...hiddenRows].forEach(({ swatch }) => container.appendChild(swatch));
    }

    let preferredVariant = null;
    let preferred = null;
    visibleRows.forEach(({ swatch, variant }) => {
      if (preferredVariantId && String(variant.id) === String(preferredVariantId)) {
        preferredVariant = swatch;
      }
      if (preferredColorKey && swatchColorKey(swatch) === preferredColorKey) {
        preferred = swatch;
      }
    });

    const target = preferredVariant || preferred || visibleRows[0]?.swatch || null;
    pinMultiActivityInitialSwatch(card, target);
    return target;
  };

  const selectSwatch = (swatch) => {
    if (!swatch) return;
    window.setTimeout(() => swatch.click(), 0);
  };

  const selectFamily = (item, familyKey, options = {}) => {
    const data = cardData(item);
    const families = visibleFamilies(data);
    if (!families.includes(familyKey)) {
      familyKey = multiActivityMatchEnabledFor(item)
        ? (multiActivityDefaultSelection(item, data)?.familyKey || "")
        : firstFamily(data);
    }
    if (!familyKey) return;

    const card = cardElement(item);
    const previousColorKey = options.preserveColor === false ? "" : swatchColorKey(activeSwatch(card));
    const interactionToken = nextInteractionToken(item);
    item.dataset.calloutV2ActiveFamily = familyKey;
    card.dataset.calloutV2ActiveFamily = familyKey;

    setFamilyButtons(item, familyKey);
    setButtonLabel(item, familyKey);
    renderFitLabel(item);
    renderLimitLabel(item, familyKey);
    renderFeatureMatrix(item, familyKey);

    const target = applyFamilyToSwatches(item, familyKey, previousColorKey, options.preferredVariantId || "");
    const targetVariant = target ? variantsById(data).get(String(target.dataset.ids || "")) : null;
    if (options.userInitiated === true && targetVariant) {
      rememberTrustedUserChoice(item, familyKey, targetVariant);
    }
    if (targetVariant) item.__calloutV2PendingVariantId = String(targetVariant.id);
    renderActivityRadar(item, targetVariant);
    selectSwatch(target);
    window.setTimeout(() => {
      if (!isCurrentInteraction(item, interactionToken)) return;
      setButtonLabel(item, familyKey);
      updateAiTryOn(item);
      renderActivityRadar(item, targetVariant);
    }, 80);
    window.setTimeout(() => {
      if (!isCurrentInteraction(item, interactionToken)) return;
      updateAiTryOn(item);
      renderActivityRadar(item, targetVariant);
      clearPendingRadarVariant(item);
    }, 360);
  };

  const hydrateItem = (item) => {
    if (item.dataset.calloutV2Hydrated === "true") return;
    const data = cardData(item);
    if (!data || !Array.isArray(data.variants)) return;

    ensureControlsPlacement(item);
    ensureOriginalSwatchIds(cardElement(item));
    observeFeatureLabelFit(item);
    item.querySelectorAll("[data-callout-v2-family]").forEach((button) => {
      button.addEventListener("click", (event) => selectFamily(
        item,
        button.dataset.calloutV2Family,
        multiActivityMatchEnabledFor(item) && event.isTrusted
          ? { userInitiated: true }
          : {}
      ));
    });
    cardElement(item)?.addEventListener("click", (event) => {
      const swatch = event.target?.closest?.(".swatch-wrapper");
      if (!swatch) return;
      const interactionToken = nextInteractionToken(item);
      const data = cardData(item);
      const targetVariant = variantsById(data).get(String((swatch.dataset.ids || "").split(",")[0] || ""));
      if (multiActivityMatchEnabledFor(item) && event.isTrusted && targetVariant) {
        pinMultiActivityInitialSwatch(cardElement(item), swatch);
        rememberTrustedUserChoice(
          item,
          item.dataset.calloutV2ActiveFamily || "",
          targetVariant
        );
      }
      if (targetVariant) {
        item.__calloutV2PendingVariantId = String(targetVariant.id);
        renderActivityRadar(item, targetVariant);
      }
      window.setTimeout(() => {
        if (!isCurrentInteraction(item, interactionToken)) return;
        updateAiTryOn(item);
        renderActivityRadar(item, targetVariant);
      }, 120);
      window.setTimeout(() => {
        if (!isCurrentInteraction(item, interactionToken)) return;
        updateAiTryOn(item);
        renderActivityRadar(item, targetVariant);
        clearPendingRadarVariant(item);
      }, 420);
    });

    item.dataset.calloutV2Hydrated = "true";
    clearInvalidTrustedUserChoice(item, data);
    const defaultSelection = userChoiceFor(item, data) || defaultSelectionFor(item, data);
    if (defaultSelection.familyKey) {
      selectFamily(item, defaultSelection.familyKey, {
        preserveColor: false,
        preferredVariantId: defaultSelection.variant?.id || "",
      });
    }
    forceFeatureLabelFontFit(item);
  };

  let hydrationObserver = null;

  const isNearViewport = (item) => {
    const rect = item.getBoundingClientRect();
    const margin = 320;
    return rect.bottom >= -margin
      && rect.top <= window.innerHeight + margin
      && rect.right >= -margin
      && rect.left <= window.innerWidth + margin;
  };

  const observerForHydration = () => {
    if (hydrationObserver || !window.IntersectionObserver) return hydrationObserver;
    hydrationObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        hydrationObserver.unobserve(entry.target);
        hydrateItem(entry.target);
      });
    }, { root: null, rootMargin: "320px", threshold: 0.01 });
    return hydrationObserver;
  };

  const queueHydration = (item) => {
    if (!item || item.dataset.calloutV2Hydrated === "true") return;
    if (isNearViewport(item)) {
      hydrateItem(item);
      return;
    }
    const observer = observerForHydration();
    if (observer) observer.observe(item);
    else hydrateItem(item);
  };

  const hydrate = (root = document) => {
    root.querySelectorAll?.("[data-callout-lens-family-v2-item]").forEach(queueHydration);
  };

  const hydrateFromInteraction = (event) => {
    const item = event.target?.closest?.("[data-callout-lens-family-v2-item]");
    if (item) hydrateItem(item);
  };
  document.addEventListener("pointerdown", hydrateFromInteraction, true);
  document.addEventListener("focusin", hydrateFromInteraction, true);

  let resizeFeatureFontFrame = null;

  const refitFeatureLabelFonts = () => {
    if (resizeFeatureFontFrame) window.cancelAnimationFrame(resizeFeatureFontFrame);
    resizeFeatureFontFrame = window.requestAnimationFrame(() => {
      resizeFeatureFontFrame = null;
      document.querySelectorAll?.('[data-callout-lens-family-v2-item][data-callout-v2-hydrated="true"]').forEach(scheduleFeatureLabelFontFit);
    });
  };

  const getOrdinaryCartTrigger = () => document.querySelector("[data-two-nu-global-dock] [data-two-nu-dock-cart-link]")
    || document.querySelector("[data-two-nu-product-dock] [data-two-nu-dock-cart-link]")
    || document.querySelector("[data-two-nu-dock-cart-link]");

  const openOrdinaryCartAfterSuccess = () => {
    const run = () => {
      const trigger = getOrdinaryCartTrigger();
      if (typeof window.twoNuOpenDockCart === "function") {
        window.twoNuOpenDockCart(trigger);
      } else if (typeof window.twoNuOpenCartDrawer === "function") {
        window.twoNuOpenCartDrawer(trigger);
      }
      if (typeof window.twoNuRefreshDockCart === "function") window.twoNuRefreshDockCart();
      document.dispatchEvent(new CustomEvent("cart:refresh"));
    };
    run();
    window.setTimeout(run, 80);
    window.setTimeout(run, 320);
  };
  window.twoNuOpenOrdinaryCartAfterAdd = () => openOrdinaryCartAfterSuccess();

  const fallbackCartAdd = async (item, button) => {
    const variant = activeVariant(item);
    if (!variant?.id || !window.routes?.cart_add_url) return;
    button?.setAttribute("aria-disabled", "true");
    button?.classList.add("loading");
    try {
      const body = {
        items: [{
          id: variant.id,
          quantity: 1,
          properties: {
            _url: variant.url || cardData(item).productUrl || window.location.pathname,
            _page_of_purchase: window.location.pathname,
            _isAddon: false,
            _isUpgradable: true,
          },
        }],
      };
      const response = await fetch(window.routes.cart_add_url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error(`Cart add failed: ${response.status}`);
      if (window.utils?.updateElements) await window.utils.updateElements([".header__icon--cart"]);
      openOrdinaryCartAfterSuccess();
      const text = buyButtonText(item);
      if (text) {
        const current = text.textContent;
        const addedLabel = cardData(item).i18n?.buttons?.added || "Added";
        text.textContent = addedLabel;
        window.setTimeout(() => {
          const family = item.dataset.calloutV2ActiveFamily || firstFamily(cardData(item));
          setButtonLabel(item, family);
          if (text.textContent === addedLabel) text.textContent = current;
        }, 1800);
      }
    } catch (error) {
      console.error("Callout v2 cart add failed", { error, marker: MARKER });
    } finally {
      button?.setAttribute("aria-disabled", "false");
      button?.classList.remove("loading");
    }
  };

  document.addEventListener("click", (event) => {
    const button = event.target?.closest?.(".callout-lens-family-v2 .buy-button");
    if (!button) return;
    const item = button.closest("[data-callout-lens-family-v2-item]");
    if (!item) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const data = cardData(item);
    const familyKey = item.dataset.calloutV2ActiveFamily || firstFamily(data);
    const action = actionForFamily(data, familyKey);
    const card = cardElement(item);
    const variant = activeVariant(item);

    if (action === "prescription") {
      button.dispatchEvent(new CustomEvent("activate-plp-prescription", {
        bubbles: true,
        detail: {
          sectionId: card?.dataset.sectionId || "",
          baseUrl: data.productUrl || button.dataset.url || "",
          id: variant?.id || card?._x_dataStack?.[0]?.id || "",
          prescriptionLimits: button.dataset.prescriptionLimits || "",
        },
      }));
      return;
    }

    if (button.dataset.twoNuOrdinaryAtcLock === "1") return;
    button.dataset.twoNuOrdinaryAtcLock = "1";
    const releaseOrdinaryAtcLock = () => {
      window.setTimeout(() => {
        delete button.dataset.twoNuOrdinaryAtcLock;
      }, 350);
    };

    const alpine = card?._x_dataStack?.[0];
    if (typeof alpine?.addToCart === "function") {
      try {
        Promise.resolve(alpine.addToCart()).finally(releaseOrdinaryAtcLock);
      } catch (error) {
        releaseOrdinaryAtcLock();
        console.error("Callout v2 alpine cart add failed", error);
      }
      return;
    }

    Promise.resolve(fallbackCartAdd(item, button)).finally(releaseOrdinaryAtcLock);
  }, true);


  const initCalloutCarousels = (root = document) => { // CODEX_CALLOUT_CAROUSEL_PEEK_ARROWS_20260702
    root.querySelectorAll?.("[data-callout-v2-carousel]").forEach((carousel) => {
      if (carousel.__calloutV2CarouselReady) return;
      const scroller = carousel.querySelector("[data-callout-v2-carousel-scroller]");
      const previousButton = carousel.querySelector("[data-callout-v2-carousel-prev]");
      const nextButton = carousel.querySelector("[data-callout-v2-carousel-next]");
      if (!scroller || !previousButton || !nextButton) return;
      carousel.__calloutV2CarouselReady = true;
      let frame = null;

      const items = () => Array.from(scroller.querySelectorAll("[data-callout-lens-family-v2-item]"))
        .filter((item) => item.offsetParent !== null);

      const maxScroll = () => Math.max(0, scroller.scrollWidth - scroller.clientWidth);

      const nearestIndex = () => {
        const current = scroller.scrollLeft;
        const list = items();
        let index = 0;
        let distance = Infinity;
        list.forEach((item, itemIndex) => {
          const offset = Math.max(0, item.offsetLeft);
          const nextDistance = Math.abs(offset - current);
          if (nextDistance < distance) {
            distance = nextDistance;
            index = itemIndex;
          }
        });
        return index;
      };

      const update = () => {
        frame = null;
        const max = maxScroll();
        const scrollable = max > 4;
        const atStart = scroller.scrollLeft <= 3;
        const atEnd = scroller.scrollLeft >= max - 3;
        previousButton.hidden = !scrollable || atStart;
        nextButton.hidden = !scrollable || atEnd;
        carousel.classList.toggle("callout-lens-family-v2__carousel--scrollable", scrollable);
      };

      const scheduleUpdate = () => {
        if (frame) return;
        frame = window.requestAnimationFrame(update);
      };

      const scrollToIndex = (delta) => {
        const list = items();
        if (!list.length) return;
        const targetIndex = Math.max(0, Math.min(list.length - 1, nearestIndex() + delta));
        scroller.scrollTo({
          left: Math.max(0, list[targetIndex].offsetLeft),
          behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ? "auto" : "smooth",
        });
      };

      previousButton.addEventListener("click", () => scrollToIndex(-1));
      nextButton.addEventListener("click", () => scrollToIndex(1));
      scroller.addEventListener("scroll", scheduleUpdate, { passive: true });
      window.addEventListener("resize", scheduleUpdate, { passive: true });
      window.requestAnimationFrame(update);
    });
  };


  document.addEventListener("DOMContentLoaded", () => {
    hydrate();
    initCalloutCarousels();
  });
  document.addEventListener("shopify:section:load", (event) => {
    hydrate(event.target);
    initCalloutCarousels(event.target);
    refitFeatureLabelFonts();
  });
  document.addEventListener("two-nu:activity-match:applied", (event) => {
    const root = event.target?.matches?.("[data-callout-lens-family-v2]")
      ? event.target
      : event.target?.closest?.("[data-callout-lens-family-v2]");
    if (!root) return;
    const selectedActivityIds = Array.isArray(event.detail?.selectedActivityIds)
      ? event.detail.selectedActivityIds
      : [];
    root.dataset.calloutActivityMatchMulti = selectedActivityIds.length >= 2
      ? "true"
      : "false";
    root.querySelectorAll("[data-callout-lens-family-v2-item]").forEach((item) => {
      delete item.__calloutLensFamilyV2Data;
      if (item.dataset.calloutV2Hydrated !== "true") {
        queueHydration(item);
        return;
      }
      const data = cardData(item);
      clearInvalidTrustedUserChoice(item, data);
      const selection = userChoiceFor(item, data) || activityDefaultSelection(item, data);
      if (!selection?.familyKey) return;
      selectFamily(item, selection.familyKey, {
        preserveColor: false,
        preferredVariantId: selection.variant?.id || "",
      });
    });
  });
  let resizeFrame = null;
  window.addEventListener("resize", () => {
    refitFeatureLabelFonts();
    if (resizeFrame) window.cancelAnimationFrame(resizeFrame);
    resizeFrame = window.requestAnimationFrame(() => {
      document.querySelectorAll("[data-callout-lens-family-v2-item]").forEach((item) => {
        if (item.__calloutV2RadarScores) renderActivityRadar(item, null, { immediate: true });
      });
    });
  });
  document.fonts?.ready?.then?.(refitFeatureLabelFonts).catch?.(() => {});
  hydrate();
  initCalloutCarousels();
})();

/* CODEX_BOGO_VISIBLE_PIP_ASSET_FALLBACK_20260703_START */
(() => {
  const marker = "CODEX_BOGO_VISIBLE_PIP_ASSET_FALLBACK_20260703";
  if (window.__twoNuBogoVisiblePipAssetFallbackReady) return;
  window.__twoNuBogoVisiblePipAssetFallbackReady = true;
  if (!/\/pages\/buy-1-get-1-free\b/i.test(window.location.pathname)) return;

  const visible = (button) => {
    if (!button || button.hidden) return false;
    const style = getComputedStyle(button);
    const rect = button.getBoundingClientRect();
    return style.display !== "none" && style.visibility !== "hidden" && rect.width > 8 && rect.height > 8;
  };

  const usable = (button) => {
    const image = button?.querySelector?.("img");
    const src = image?.currentSrc || image?.src || "";
    return !!button && !!image && src && !src.startsWith("data:image/gif");
  };

  const repairCard = (card) => {
    const buttons = Array.from(card.querySelectorAll("button.callout-pip"));
    if (!buttons.length || buttons.some(visible)) return;
    const fallback = buttons.find(usable) || buttons[0];
    buttons.forEach((button) => {
      const active = button === fallback;
      button.hidden = !active;
      button.toggleAttribute("hidden", !active);
      button.classList.toggle("is-active", active);
    });
    if (fallback) {
      fallback.style.removeProperty("display");
      fallback.style.removeProperty("visibility");
      fallback.style.removeProperty("opacity");
    }
  };

  const repairAll = () => {
    document
      .querySelectorAll("#summer-products .card-js, #summer-running-caps .card-js")
      .forEach(repairCard);
    document.documentElement.setAttribute("data-two-nu-bogo-visible-pip-fallback", "ok");
  };

  const schedule = () => {
    repairAll();
    setTimeout(repairAll, 80);
    setTimeout(repairAll, 240);
    setTimeout(repairAll, 700);
    setTimeout(repairAll, 1600);
  };

  document.addEventListener("click", (event) => {
    if (event.target?.closest?.("#summer-products .swatch-wrapper, #summer-products [data-callout-v2-family], #summer-running-caps .swatch-wrapper")) {
      schedule();
    }
  }, false);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", schedule, { once: true });
  } else {
    schedule();
  }

  new MutationObserver(() => schedule()).observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["hidden", "class", "style"],
  });
})();
/* CODEX_BOGO_VISIBLE_PIP_ASSET_FALLBACK_20260703_END */
